import { useEffect, useRef, useState } from 'react'
import { useAnnotation } from '@embedpdf/plugin-annotation/react'
import { PdfAnnotationSubtype, Position } from '@embedpdf/models'
import { getCurrentStyle, resolveDashArray } from '../../stores/styleStore'

const TOOL_MAPPING: Record<string, PdfAnnotationSubtype> = {
  highlight: PdfAnnotationSubtype.HIGHLIGHT,
  strikeout: PdfAnnotationSubtype.STRIKEOUT,
  strikethrough: PdfAnnotationSubtype.STRIKEOUT,
  underline: PdfAnnotationSubtype.UNDERLINE,
  squiggly: PdfAnnotationSubtype.SQUIGGLY,
  squigglyUnderline: PdfAnnotationSubtype.SQUIGGLY,
  ink: PdfAnnotationSubtype.INK,
  inkHighlighter: PdfAnnotationSubtype.INK,
  freeText: PdfAnnotationSubtype.FREETEXT,
  freeTextCallout: PdfAnnotationSubtype.TEXT,
  textCallout: PdfAnnotationSubtype.TEXT,
  typewriter: PdfAnnotationSubtype.TEXT,
  stickyNote: PdfAnnotationSubtype.TEXT,
  textComment: PdfAnnotationSubtype.TEXT,
  stamp: PdfAnnotationSubtype.STAMP,
  rubberStamp: PdfAnnotationSubtype.STAMP,
  rectangle: PdfAnnotationSubtype.SQUARE,
  square: PdfAnnotationSubtype.SQUARE,
  oval: PdfAnnotationSubtype.CIRCLE,
  circle: PdfAnnotationSubtype.CIRCLE,
  line: PdfAnnotationSubtype.LINE,
  arrow: PdfAnnotationSubtype.LINE,
  lineArrow: PdfAnnotationSubtype.LINE,
  polygon: PdfAnnotationSubtype.POLYGON,
  polyline: PdfAnnotationSubtype.POLYLINE,
  redactArea: PdfAnnotationSubtype.REDACT,
  redactText: PdfAnnotationSubtype.REDACT
}

interface AnnotationDrawLayerProps {
  documentId: string
  pageIndex: number
}

export const AnnotationDrawLayer = ({ documentId, pageIndex }: AnnotationDrawLayerProps) => {
  const { provides: annotationApi, state: annotationState } = useAnnotation(documentId)
  const activeTool = annotationState?.activeToolId || null

  const [isDrawing, setIsDrawing] = useState(false)
  const [startPos, setStartPos] = useState<Position | null>(null)
  const [currentPos, setCurrentPos] = useState<Position | null>(null)

  useEffect(() => {
    const body = document.body
    const html = document.documentElement

    const textTools = new Set(['freeText', 'textCallout', 'stickyNote', 'textComment', 'highlight', 'strikethrough', 'underline', 'squigglyUnderline'])
    const crosshairTools = new Set(['ink', 'inkHighlighter', 'rectangle', 'oval', 'line', 'arrow', 'polygon', 'polyline', 'redactArea', 'redactText'])

    body.classList.remove('annotation-cursor-text', 'annotation-cursor-crosshair')
    html.classList.remove('annotation-cursor-text', 'annotation-cursor-crosshair')

    if (!activeTool) {
      return
    }

    if (textTools.has(activeTool)) {
      body.classList.add('annotation-cursor-text')
      html.classList.add('annotation-cursor-text')
    } else if (crosshairTools.has(activeTool)) {
      body.classList.add('annotation-cursor-crosshair')
      html.classList.add('annotation-cursor-crosshair')
    }

    return () => {
      body.classList.remove('annotation-cursor-text', 'annotation-cursor-crosshair')
      html.classList.remove('annotation-cursor-text', 'annotation-cursor-crosshair')
    }
  }, [activeTool])

  const drawingRef = useRef(false)

  useEffect(() => {
    if (!activeTool) {
      return
    }

    const toolType = TOOL_MAPPING[activeTool]
    if (!toolType) {
      console.warn('Unknown tool type:', activeTool)
      return
    }

    const handleMouseDown = (_e: MouseEvent) => {
      const target = _e.target as HTMLElement

      if (!annotationApi || target.closest('.annotation-layer') === null) {
        return
      }

      // Don't intercept if clicking on an existing annotation to drag it
      const annotationElement = target.closest('[data-no-interaction]')
      if (annotationElement) {
        return
      }

      // Don't start drawing if an annotation is already selected (allow dragging instead)
      const selectedAnnotation = annotationApi.getSelectedAnnotation()
      if (selectedAnnotation) {
        return
      }

      const rect = target.getBoundingClientRect()
      const x = _e.clientX - rect.left
      const y = _e.clientY - rect.top

      setIsDrawing(true)
      setStartPos({ x, y })
      setCurrentPos({ x, y })
      drawingRef.current = true

      if (toolType === PdfAnnotationSubtype.INK) {
        const canvas = target as HTMLCanvasElement
        const ctx = canvas?.getContext('2d')
        if (ctx) {
          ctx.beginPath()
          ctx.moveTo(x, y)
        }
      }
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDrawing || !startPos || !currentPos || !annotationApi) {
        return
      }

      const target = e.target as HTMLElement
      const rect = target.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      setCurrentPos({ x, y })

      if (TOOL_MAPPING[activeTool] === PdfAnnotationSubtype.INK) {
        const canvas = target as HTMLCanvasElement
        const ctx = canvas?.getContext('2d')
        if (ctx) {
          const style = getCurrentStyle()
          ctx.lineWidth = style.strokeWidth
          ctx.strokeStyle = style.strokeColor
          ctx.lineCap = 'round'
          ctx.lineJoin = 'round'
          ctx.lineTo(x, y)
          ctx.stroke()
        }
      }
    }

    const handleMouseUp = (_e: MouseEvent) => {
      if (!isDrawing || !startPos || !currentPos || !annotationApi) {
        return
      }

      const toolType = TOOL_MAPPING[activeTool]
      if (!toolType) {
        setIsDrawing(false)
        drawingRef.current = false
        return
      }

      setIsDrawing(false)
      drawingRef.current = false

      const style = getCurrentStyle()

      const width = currentPos.x - startPos.x
      const height = currentPos.y - startPos.y

      const annotationRect = {
        origin: {
          x: width >= 0 ? startPos.x : currentPos.x,
          y: height >= 0 ? startPos.y : currentPos.y
        },
        size: {
          width: Math.abs(width),
          height: Math.abs(height)
        }
      }

      let annotation: any
      const dashArray = resolveDashArray(style.borderStyle, style.strokeWidth)

      switch (toolType) {
        case PdfAnnotationSubtype.HIGHLIGHT:
          annotation = {
            id: crypto.randomUUID(),
            type: PdfAnnotationSubtype.HIGHLIGHT,
            rect: annotationRect,
            pageIndex,
            color: style.strokeColor,
            opacity: style.opacity,
            segmentRects: [annotationRect],
            contents: '',
            author: 'User',
            created: new Date(),
            modified: new Date(),
            flags: ['print'],
            rotation: style.rotation || undefined,
          }
          break

        case PdfAnnotationSubtype.INK:
          annotation = {
            id: crypto.randomUUID(),
            type: PdfAnnotationSubtype.INK,
            rect: annotationRect,
            pageIndex,
            inkList: [],
            strokeWidth: style.strokeWidth,
            color: style.strokeColor,
            opacity: style.opacity,
            contents: '',
            author: 'User',
            created: new Date(),
            modified: new Date(),
            flags: ['print'],
            rotation: style.rotation || undefined,
          }
          break

        case PdfAnnotationSubtype.SQUARE:
        case PdfAnnotationSubtype.REDACT:
          annotation = {
            id: crypto.randomUUID(),
            type: toolType,
            rect: annotationRect,
            pageIndex,
            color: style.fillColor,
            strokeColor: style.strokeColor,
            strokeWidth: style.strokeWidth,
            opacity: style.opacity,
            ...(dashArray ? { strokeDashArray: dashArray } : {}),
            ...(style.borderStyle === 'cloudy' ? { cloudyBorderIntensity: 2 } : {}),
            contents: '',
            author: 'User',
            created: new Date(),
            modified: new Date(),
            flags: ['print'],
            rotation: style.rotation || undefined,
          }
          break

        case PdfAnnotationSubtype.CIRCLE:
          annotation = {
            id: crypto.randomUUID(),
            type: PdfAnnotationSubtype.CIRCLE,
            rect: annotationRect,
            pageIndex,
            color: style.fillColor,
            strokeColor: style.strokeColor,
            strokeWidth: style.strokeWidth,
            opacity: style.opacity,
            ...(dashArray ? { strokeDashArray: dashArray } : {}),
            ...(style.borderStyle === 'cloudy' ? { cloudyBorderIntensity: 2 } : {}),
            contents: '',
            author: 'User',
            created: new Date(),
            modified: new Date(),
            flags: ['print'],
            rotation: style.rotation || undefined,
          }
          break

        case PdfAnnotationSubtype.LINE:
          annotation = {
            id: crypto.randomUUID(),
            type: PdfAnnotationSubtype.LINE,
            rect: annotationRect,
            pageIndex,
            linePoints: {
              start: { x: annotationRect.origin.x, y: annotationRect.origin.y },
              end: {
                x: annotationRect.origin.x + annotationRect.size.width,
                y: annotationRect.origin.y + annotationRect.size.height
              }
            },
            strokeColor: style.strokeColor,
            strokeWidth: style.strokeWidth,
            opacity: style.opacity,
            ...(dashArray ? { strokeDashArray: dashArray } : {}),
            contents: '',
            author: 'User',
            created: new Date(),
            modified: new Date(),
            flags: ['print'],
            rotation: style.rotation || undefined,
          }
          break

        case PdfAnnotationSubtype.POLYGON:
          annotation = {
            id: crypto.randomUUID(),
            type: PdfAnnotationSubtype.POLYGON,
            rect: annotationRect,
            pageIndex,
            vertices: [
              { x: annotationRect.origin.x, y: annotationRect.origin.y },
              { x: annotationRect.origin.x + annotationRect.size.width, y: annotationRect.origin.y },
              {
                x: annotationRect.origin.x + annotationRect.size.width,
                y: annotationRect.origin.y + annotationRect.size.height
              },
              { x: annotationRect.origin.x, y: annotationRect.origin.y + annotationRect.size.height }
            ],
            color: style.fillColor,
            strokeColor: style.strokeColor,
            strokeWidth: style.strokeWidth,
            opacity: style.opacity,
            ...(dashArray ? { strokeDashArray: dashArray } : {}),
            ...(style.borderStyle === 'cloudy' ? { cloudyBorderIntensity: 2 } : {}),
            contents: '',
            author: 'User',
            created: new Date(),
            modified: new Date(),
            flags: ['print'],
            rotation: style.rotation || undefined,
          }
          break

        case PdfAnnotationSubtype.POLYLINE:
          annotation = {
            id: crypto.randomUUID(),
            type: PdfAnnotationSubtype.POLYLINE,
            rect: annotationRect,
            pageIndex,
            vertices: [
              { x: annotationRect.origin.x, y: annotationRect.origin.y },
              { x: annotationRect.origin.x + annotationRect.size.width, y: annotationRect.origin.y },
              {
                x: annotationRect.origin.x + annotationRect.size.width,
                y: annotationRect.origin.y + annotationRect.size.height
              },
              { x: annotationRect.origin.x, y: annotationRect.origin.y + annotationRect.size.height }
            ],
            strokeColor: style.strokeColor,
            strokeWidth: style.strokeWidth,
            opacity: style.opacity,
            ...(dashArray ? { strokeDashArray: dashArray } : {}),
            contents: '',
            author: 'User',
            created: new Date(),
            modified: new Date(),
            flags: ['print'],
            rotation: style.rotation || undefined,
          }
          break

        case PdfAnnotationSubtype.FREETEXT:
          annotation = {
            id: crypto.randomUUID(),
            type: PdfAnnotationSubtype.FREETEXT,
            rect: annotationRect,
            pageIndex,
            contents: 'Type here...',
            fontSize: style.freeTextStyle.fontSize,
            fontColor: style.freeTextStyle.fontColor,
            fontFamily: style.freeTextStyle.fontFamily,
            textAlign: style.freeTextStyle.textAlign,
            verticalAlign: style.freeTextStyle.verticalAlign,
            color: style.fillColor || undefined,
            strokeColor: style.strokeColor,
            strokeWidth: style.strokeWidth || undefined,
            opacity: style.opacity,
            author: 'User',
            created: new Date(),
            modified: new Date(),
            flags: ['print'],
            rotation: style.rotation || undefined,
          }
          break

        case PdfAnnotationSubtype.TEXT:
          const isCommentTool = activeTool === 'stickyNote' || activeTool === 'textComment'
          let textRect = annotationRect
          if (isCommentTool) {
            const iconSize = 24
            textRect = {
              origin: { x: startPos.x - iconSize / 2, y: startPos.y - iconSize / 2 },
              size: { width: iconSize, height: iconSize }
            }
          }
          annotation = {
            id: crypto.randomUUID(),
            type: PdfAnnotationSubtype.TEXT,
            rect: textRect,
            pageIndex,
            contents: isCommentTool ? '' : 'Comment...',
            icon: 'Comment',
            opacity: style.opacity,
            author: 'User',
            created: new Date(),
            modified: new Date(),
            flags: ['print'],
            rotation: style.rotation || undefined,
          }
          break

        case PdfAnnotationSubtype.STAMP:
          annotation = {
            id: crypto.randomUUID(),
            type: PdfAnnotationSubtype.STAMP,
            rect: annotationRect,
            pageIndex,
            icon: 'Stamp',
            contents: '',
            opacity: style.opacity,
            author: 'User',
            created: new Date(),
            modified: new Date(),
            flags: ['print'],
            rotation: style.rotation || undefined,
          }
          break

        default:
          annotation = {
            id: crypto.randomUUID(),
            type: toolType,
            rect: annotationRect,
            pageIndex,
            contents: '',
            author: 'User',
            created: new Date(),
            modified: new Date(),
            flags: ['print']
          }
      }

      try {
        if (annotationApi.createAnnotation) {
          annotationApi.createAnnotation(pageIndex, annotation)
          if (annotationApi.selectAnnotation && annotation.id) {
            annotationApi.selectAnnotation(pageIndex, annotation.id)
          }
        }
      } catch (error) {
        console.error('Failed to create annotation:', error)
      }

      setStartPos(null)
      setCurrentPos(null)
    }

    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [activeTool, isDrawing, startPos, currentPos, annotationApi, documentId, pageIndex])

  return null
}
